import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  Paper,
} from '@mui/material';
import {
  Edit as EditIcon,
  Preview as PreviewIcon,
  Check as CheckIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { adminApi } from '../../api';
import type { EmailTemplate, UpdateEmailTemplateInput } from '../../api/admin';
import { isAdmin } from '../../types';
import { useAuth } from '../../hooks/useAuth';
import { useDocumentTitle } from '../../hooks/core';
import { sanitizeHtml } from '@katapult/ui';
import { AdminBreadcrumbs } from '../../components/admin';

function formatTemplateType(type: string): string {
  return type
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (l) => l.toUpperCase());
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

export function EmailTemplatesPage() {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();

  useDocumentTitle('Email Templates');

  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);
  const [editForm, setEditForm] = useState<UpdateEmailTemplateInput>({});
  const [editTab, setEditTab] = useState(0);
  const [saving, setSaving] = useState(false);

  // Preview dialog state
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState<string>('');
  const [previewSubject, setPreviewSubject] = useState<string>('');

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const data = await adminApi.getEmailTemplates();
      setTemplates(data);
    } catch (err) {
      setError('Failed to load email templates');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAdmin(currentUser)) {
      navigate('/');
      return;
    }
    fetchTemplates();
  }, [currentUser, navigate, fetchTemplates]);

  const handleEditClick = (template: EmailTemplate) => {
    setEditingTemplate(template);
    setEditForm({
      name: template.name,
      subject: template.subject,
      htmlBody: template.htmlBody,
      textBody: template.textBody,
      active: template.active,
    });
    setEditTab(0);
    setEditDialogOpen(true);
  };

  const handleEditClose = () => {
    setEditDialogOpen(false);
    setEditingTemplate(null);
    setEditForm({});
  };

  const handleEditSave = async () => {
    if (!editingTemplate) return;

    try {
      setSaving(true);
      await adminApi.updateEmailTemplate(editingTemplate.type, editForm);
      await fetchTemplates();
      handleEditClose();
    } catch (err) {
      setError('Failed to save template');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handlePreviewClick = async (template: EmailTemplate) => {
    try {
      const result = await adminApi.previewEmailTemplate({
        templateType: template.type,
      });
      setPreviewSubject(result.subject);
      setPreviewHtml(result.htmlBody);
      setPreviewDialogOpen(true);
    } catch (err) {
      setError('Failed to generate preview');
      console.error(err);
    }
  };

  if (loading && templates.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <AdminBreadcrumbs items={[{ label: 'Email' }, { label: 'Templates' }]} />
      <Typography variant="h4" sx={{ mb: 3 }}>
        Email Templates
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Subject</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {templates.map((template) => (
                <TableRow key={template.id} hover>
                  <TableCell>
                    <Typography variant="body2">
                      {template.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        maxWidth: 300,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {template.subject}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={template.active ? 'Active' : 'Inactive'}
                      color={template.active ? 'success' : 'default'}
                      size="small"
                      icon={template.active ? <CheckIcon /> : <CloseIcon />}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Preview">
                      <IconButton
                        size="small"
                        onClick={() => handlePreviewClick(template)}
                      >
                        <PreviewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => handleEditClick(template)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {templates.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No email templates found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={handleEditClose}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Edit Template: {editingTemplate && formatTemplateType(editingTemplate.type)}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2, mt: 1 }}>
            <TextField
              fullWidth
              label="Template Name"
              value={editForm.name || ''}
              onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Subject Line"
              value={editForm.subject || ''}
              onChange={(e) => setEditForm({ ...editForm, subject: e.target.value })}
              sx={{ mb: 2 }}
              helperText="Supports Go template variables like {{.RecipientName}}"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={editForm.active ?? true}
                  onChange={(e) => setEditForm({ ...editForm, active: e.target.checked })}
                />
              }
              label="Active"
            />
          </Box>

          <Paper variant="outlined" sx={{ mb: 2 }}>
            <Tabs
              value={editTab}
              onChange={(_, v) => setEditTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
            >
              <Tab label="HTML Body" />
              <Tab label="Text Body" />
              <Tab label="Variables" />
            </Tabs>
            <Box sx={{ p: 2 }}>
              <TabPanel value={editTab} index={0}>
                <TextField
                  fullWidth
                  multiline
                  rows={15}
                  value={editForm.htmlBody || ''}
                  onChange={(e) => setEditForm({ ...editForm, htmlBody: e.target.value })}
                  placeholder="HTML email body..."
                  sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}
                  inputProps={{ style: { fontFamily: 'monospace' } }}
                />
              </TabPanel>
              <TabPanel value={editTab} index={1}>
                <TextField
                  fullWidth
                  multiline
                  rows={15}
                  value={editForm.textBody || ''}
                  onChange={(e) => setEditForm({ ...editForm, textBody: e.target.value })}
                  placeholder="Plain text email body..."
                  inputProps={{ style: { fontFamily: 'monospace' } }}
                />
              </TabPanel>
              <TabPanel value={editTab} index={2}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Available template variables (read-only). Use these in your template with {'{{.VariableName}}'} syntax.
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {editingTemplate?.variables?.map((variable) => (
                    <Chip
                      key={variable}
                      label={`{{.${variable}}}`}
                      variant="outlined"
                      size="small"
                      sx={{ fontFamily: 'monospace' }}
                    />
                  ))}
                  {(!editingTemplate?.variables || editingTemplate.variables.length === 0) && (
                    <Typography variant="body2" color="text.secondary">
                      No variables defined for this template.
                    </Typography>
                  )}
                </Box>
              </TabPanel>
            </Box>
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditClose}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleEditSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog
        open={previewDialogOpen}
        onClose={() => setPreviewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Email Preview
          <Typography variant="body2" color="text.secondary">
            Subject: {previewSubject}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Paper
            variant="outlined"
            sx={{ p: 2, bgcolor: 'white', minHeight: 400 }}
          >
            <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(previewHtml) }} />
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
